import { CSSProperties, FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Icons, Tooltip, Typography } from '../../../../stories/atoms';
import { Colors } from '../../../../theme';
import { homeTranslations } from '../../../../translations';
import { StyledLegend, TileWrapper } from './WidgetMetricTile.styles';

export type ProgressIndicatorStatus = 'SAD' | 'CONFUSED' | 'HAPPY';

export enum WidgetMetricType {
  ForecastMetric = 'forecast',
  RegularMetric = 'regular',
  LegendMetric = 'legend',
}

export type ForecastMetricProperties = {
  metricValue: string;
  forecastIndicator: ProgressIndicatorStatus;
  metricTooltipText?: string;
};

export type RegularMetricProperties = {
  metricTitle: string;
  metricValue: string | JSX.Element;
  metricTooltipText?: string;
};

export type LegendMetricProperties = RegularMetricProperties & {
  legendColor: Colors;
};
export type WidgetMetric =
  | { type: WidgetMetricType.ForecastMetric; properties: ForecastMetricProperties }
  | { type: WidgetMetricType.RegularMetric; properties: RegularMetricProperties }
  | { type: WidgetMetricType.LegendMetric; properties: LegendMetricProperties };

export type MetricTileProps = {
  metricProperties: WidgetMetric;
  isLoading?: boolean;
  isError?: boolean;
  style?: CSSProperties;
};

export const WidgetMetricTile: FC<MetricTileProps> = ({
  metricProperties,
  isLoading = false,
  isError = false,
  style,
}) => {
  const { t } = useTranslation();
  const { common: commonTranslations } = homeTranslations;
  const { type, properties } = metricProperties;

  const wrapTileWithTooltip = (tile: ReactElement, tooltipText?: string) => {
    if (!tooltipText) return tile;

    return <Tooltip text={<Typography color="white">{tooltipText}</Typography>}>{tile}</Tooltip>;
  };

  const renderMetricTitle = (title: string, testId?: string) => {
    return (
      <Typography type="caption" color="textGray" data-testid={testId}>
        {title}:
      </Typography>
    );
  };

  return (
    <>
      {isLoading && (
        <TileWrapper style={style} data-testid={`loading-tile`}>
          <Typography type="caption" color="textGray">
            {t(commonTranslations.loading.key, commonTranslations.loading.defaultValue)}
          </Typography>
          <Icon icon={Icons.SPINNER} size="small" data-testid={`loading-icon`} />
        </TileWrapper>
      )}
      {!isLoading && isError && (
        <TileWrapper style={style} data-testid={`error-tile`}>
          <Typography type="caption" color="errorLight">
            {t(commonTranslations.error.key, commonTranslations.error.defaultValue)}
          </Typography>
        </TileWrapper>
      )}

      {!isLoading && !isError && (
        <>
          {type === 'regular' && (
            <>
              {wrapTileWithTooltip(
                <TileWrapper
                  style={style}
                  data-testid={`${type}-tile`}
                  tabIndex={properties.metricTooltipText ? 0 : undefined}
                >
                  {renderMetricTitle(properties.metricTitle, 'regular-metric-title')}
                  {typeof properties.metricValue === 'string' ? (
                    <Typography type="caption">{properties.metricValue}</Typography>
                  ) : (
                    properties.metricValue
                  )}
                </TileWrapper>,
                properties.metricTooltipText,
              )}
            </>
          )}
          {type === 'legend' && (
            <>
              {wrapTileWithTooltip(
                <TileWrapper
                  style={style}
                  data-testid={`${type}-tile`}
                  tabIndex={properties.metricTooltipText ? 0 : undefined}
                >
                  <StyledLegend legendColor={properties.legendColor} />
                  {renderMetricTitle(properties.metricTitle, 'legend-metric-title')}
                  <Typography type="caption">{properties.metricValue}</Typography>
                </TileWrapper>,
                properties.metricTooltipText,
              )}
            </>
          )}

          {type === 'forecast' &&
            (() => {
              const { forecastIndicator, metricValue, metricTooltipText } = properties;
              const forecastTitle = t(commonTranslations.forecast.key, commonTranslations.forecast.defaultValue);

              return wrapTileWithTooltip(
                <TileWrapper
                  style={style}
                  indicator={forecastIndicator}
                  data-testid={`${type}-tile`}
                  tabIndex={metricTooltipText ? 0 : undefined}
                >
                  {renderMetricTitle(forecastTitle, 'forecast-metric-title')}
                  <Typography type="caption">{metricValue}</Typography>
                  <Icon icon={Icons[forecastIndicator]} size="small" data-testid={forecastIndicator} />
                </TileWrapper>,
                metricTooltipText,
              );
            })()}
        </>
      )}
    </>
  );
};
